import { IconButton, SyllabusIcon, UserIcon } from "@minervaproject/fds";
import React from "react";

import { useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { featureBreakoutRoomResource } from "shared/reducers/classroom";
import { BreakoutRoomType, CapabilityType } from "shared/types";
import { UserType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { featureBreakoutRoom } from "utils/classroom";

function FeatureBreakoutRoomButtons({
  breakoutRoom,
  allUsers,
}: {
  breakoutRoom: BreakoutRoomType;
  allUsers: UserType[];
}) {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const maxSubscribedStreams = useAppSelector(classroomSelectors.selectMaxSubscribedStreams());
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const presentVisibleUsers = allUsers.filter(
    (user) => user.is_present && isVisibleInClassUserIds.includes(user.id),
  );
  const isAlwaysSubscribedToUserIds = useUserIdsWithCapability(
    CapabilityType.IS_ALWAYS_SUBSCRIBED_TO,
  );
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const participantBarUserIds = useAppSelector(classroomSelectors.selectParticipantBarUserIds());
  const resources = useArraySelector(
    classroomSelectors.selectResourcesByIds(breakoutRoom?.resource_ids || []),
  );
  const breakoutNotes = resources.filter((resource) => resource.is_breakout_group_notes)[0];

  const handleFeatureBreakoutGroup = (displayResource: boolean) => () => {
    if (isInPerson) {
      dispatch(featureBreakoutRoomResource({ breakoutRoomId: breakoutRoom.id }));
    } else {
      featureBreakoutRoom(
        dispatch,
        breakoutRoom,
        displayResource ? breakoutNotes : undefined,
        maxSubscribedStreams,
        currentUserId,
        presentVisibleUsers,
        isAlwaysSubscribedToUserIds,
        canParticipateInClassUserIds,
        participantBarUserIds,
      );
    }
  };

  return (
    <div className="flex items-center">
      <span className="body-xs bold text-black-tint-70 mr4">Feature:</span>
      <IconButton
        onClick={handleFeatureBreakoutGroup(true)}
        aria-label="Feature Resource"
        className="mr4"
      >
        <SyllabusIcon width={16} height={16} color="#b3b3b3" />
        <span className="text-black-tint-70 body-xs bold ml1">Resource</span>
      </IconButton>
      <IconButton onClick={handleFeatureBreakoutGroup(false)} aria-label="Feature People">
        <UserIcon width={16} height={16} stroke="#b3b3b3" />
        <span className="text-black-tint-70 body-xs bold ml1">People</span>
      </IconButton>
    </div>
  );
}

export { FeatureBreakoutRoomButtons };
